import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Award, Car, BadgeCheck, ArrowRight, Quote, Star } from 'lucide-react';
import BookingWidget from '../components/BookingWidget';
import { useI18n } from '../i18n';
import { services, useCases, cities, testimonials, excursions } from '../mock';
import { Button } from '../components/ui/button';
import HoverVideoCard from '../components/HoverVideoCard';

const Home = () => {
  const { t, lang } = useI18n();

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative bg-black text-white pt-[72px]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-60"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1646644431825-e5171c8cba53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzZWRhbnxlbnwwfHx8YmxhY2t8MTc3ODA2NjcwNHww&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-white/70">Ha Transport • Roissy CDG</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-5">
              {t('hero.title')}
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">{t('hero.subtitle')}</p>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-white/70">
              <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> 24/7</span>
              <span className="flex items-center gap-2"><Star className="w-4 h-4" /> 4.9 / 5</span>
              <span className="flex items-center gap-2"><Award className="w-4 h-4" /> Premium fleet</span>
            </div>
          </div>
          <div className="lg:pl-10">
            <BookingWidget variant="hero" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[11px] tracking-[0.4em] uppercase text-black/50">Ha Transport</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3">{t('features.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mt-14">
            {[
              { icon: Clock, t: t('features.f1.t'), d: t('features.f1.d') },
              { icon: Award, t: t('features.f2.t'), d: t('features.f2.d') },
              { icon: Car, t: t('features.f3.t'), d: t('features.f3.d') },
              { icon: BadgeCheck, t: t('features.f4.t'), d: t('features.f4.d') }
            ].map((f, i) => (
              <div key={i} className="group">
                <div className="w-12 h-12 border border-black/15 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl mt-6">{f.t}</h3>
                <p className="text-black/60 mt-3 leading-relaxed text-[15px]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-20 lg:py-28 bg-[#f6f4f1]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl md:text-5xl">{t('usecases.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {useCases.map(uc => (
              <Link key={uc.id} to="/services" className="group bg-white overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={uc.image} alt={lang === 'fr' ? uc.keyFr : uc.keyEn} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl">{lang === 'fr' ? uc.keyFr : uc.keyEn}</h3>
                  <p className="text-black/60 mt-2 text-sm leading-relaxed">{lang === 'fr' ? uc.descFr : uc.descEn}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium">
                    {lang === 'fr' ? 'Découvrir' : 'Discover'} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET PREVIEW */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-[11px] tracking-[0.4em] uppercase text-black/50">Fleet</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-3">{t('fleet.title')}</h2>
              <p className="text-black/60 mt-3">{t('fleet.subtitle')}</p>
            </div>
            <Link to="/fleet" className="text-sm tracking-[0.2em] uppercase border-b border-black hover:text-black/70">
              {lang === 'fr' ? 'Voir toute la flotte' : 'See full fleet'}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {services.slice(0, 3).map(s => (
              <div key={s.id} className="bg-white border border-black/10 hover:shadow-xl transition-shadow">
                <div className="aspect-[16/10] overflow-hidden bg-[#eee]">
                  <img src={s.image} alt={s.nameFr} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl">{lang === 'fr' ? s.nameFr : s.nameEn}</h3>
                  <div className="flex gap-4 text-xs uppercase tracking-wider text-black/50 mt-3">
                    <span>{s.pax} {t('fleet.pax')}</span>
                    <span>•</span>
                    <span>{s.luggage} {t('fleet.luggage')}</span>
                  </div>
                  <p className="text-black/60 mt-4 text-sm leading-relaxed line-clamp-3">{lang === 'fr' ? s.descFr : s.descEn}</p>
                  <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-sm font-medium">
                    {t('fleet.book')} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISNEYLAND FEATURED */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative aspect-[16/10] overflow-hidden bg-black group">
              <HoverVideoCard
                image="https://images.unsplash.com/photo-1598961336118-93106a3cab26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxEaXNuZXlsYW5kJTIwUGFyaXMlMjBjYXN0bGV8ZW58MHx8fHwxNzc4MTQxOTY5fDA&ixlib=rb-4.1.0&q=85"
                youtubeId="Lc2K1srGvbk"
                alt="Disneyland Paris"
              />
              <div className="absolute top-4 left-4 z-10 bg-white text-black px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-medium pointer-events-none">
                {lang === 'fr' ? 'Forfait' : 'Flat rate'}
              </div>
            </div>
            <div>
              <p className="text-[11px] tracking-[0.4em] uppercase text-black/50">
                {lang === 'fr' ? 'Destination phare' : 'Featured destination'}
              </p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">Disneyland Paris</h2>
              <p className="text-black/60 mt-5 leading-relaxed text-lg">
                {lang === 'fr'
                  ? 'Profitez d\u2019un transfert direct vers Disneyland Paris depuis l\u2019a\u00e9roport CDG, Orly ou Paris. Chauffeur priv\u00e9, accueil personnalis\u00e9, suivi de vol et si\u00e8ges enfants sur demande.'
                  : 'Enjoy a direct transfer to Disneyland Paris from CDG, Orly or Paris. Private chauffeur, meet & greet, flight tracking and child seats on request.'}
              </p>

              <div className="mt-8 flex items-end gap-6">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-black/50">{lang === 'fr' ? '\u00c0 partir de' : 'From'}</p>
                  <p className="font-serif text-5xl md:text-6xl mt-1">85 €</p>
                </div>
                <div className="pb-2 text-sm text-black/60 leading-tight">
                  <p>{lang === 'fr' ? 'gamme Standard' : 'Standard range'}</p>
                  <p className="text-xs text-black/50">{lang === 'fr' ? 'par trajet \u2022 forfait' : 'per trip \u2022 flat rate'}</p>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4 text-center border-t border-b border-black/10 py-5">
                <div>
                  <p className="font-serif text-2xl">85 €</p>
                  <p className="text-[10px] tracking-widest uppercase text-black/50 mt-1">Standard</p>
                </div>
                <div>
                  <p className="font-serif text-2xl">129 €</p>
                  <p className="text-[10px] tracking-widest uppercase text-black/50 mt-1">Van</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-black/40">—</p>
                  <p className="text-[10px] tracking-widest uppercase text-black/50 mt-1">{lang === 'fr' ? 'Autres : devis' : 'Other: quote'}</p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button className="bg-black hover:bg-black/85 text-white rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">
                    {lang === 'fr' ? 'R\u00e9server' : 'Book now'}
                  </Button>
                </Link>
                <a href="tel:+33751284124">
                  <Button variant="outline" className="border-black text-black hover:bg-black hover:text-white rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">
                    {lang === 'fr' ? 'Appeler' : 'Call us'}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXCURSIONS */}
      <section className="py-20 lg:py-28 bg-[#f6f4f1]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-[11px] tracking-[0.4em] uppercase text-black/50">{lang === 'fr' ? 'Excursions' : 'Day trips'}</p>
              <h2 className="font-serif text-4xl md:text-5xl mt-3">
                {lang === 'fr' ? 'D\u00e9couvrez la France avec chauffeur' : 'Discover France by chauffeur'}
              </h2>
              <p className="text-black/60 mt-3 max-w-xl">
                {lang === 'fr'
                  ? 'Giverny, Mont-Saint-Michel, plages du D\u00e9barquement, ch\u00e2teaux de la Loire, maisons de Champagne\u2026'
                  : 'Giverny, Mont-Saint-Michel, D-Day beaches, Loire Valley castles, Champagne houses\u2026'}
              </p>
            </div>
            <Link to="/excursions" className="text-sm tracking-[0.2em] uppercase border-b border-black hover:text-black/70">
              {lang === 'fr' ? 'Voir toutes les excursions' : 'See all day trips'}
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {excursions.slice(0, 4).map(e => (
              <Link to="/excursions" key={e.id} className="group relative overflow-hidden aspect-[4/5]">
                <img src={e.image} alt={e.nameFr} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-white/70">{e.region.split(',')[0]}</p>
                  <p className="font-serif text-xl mt-1">{lang === 'fr' ? e.nameFr : e.nameEn}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="py-20 lg:py-28 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl md:text-5xl max-w-3xl">{t('cities.title')}</h2>
          <p className="text-white/60 mt-3 max-w-xl">{t('cities.subtitle')}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {cities.slice(0, 8).map(c => (
              <div key={c.name} className="group relative overflow-hidden aspect-[4/5]">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="font-serif text-xl">{c.name}</p>
                  <p className="text-xs text-white/60 tracking-widest uppercase">{c.country}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl md:text-5xl">{t('testimonials.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {testimonials.map(tm => (
              <div key={tm.id} className="border border-black/10 p-8">
                <Quote className="w-7 h-7 text-black/20" />
                <p className="mt-5 text-[17px] leading-relaxed font-serif">“{lang === 'fr' ? tm.quoteFr : tm.quoteEn}”</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-xs">{tm.nameFr.split(' ')[0][0]}</div>
                  <div>
                    <p className="text-sm font-medium">{tm.nameFr}</p>
                    <p className="text-xs text-black/50">{tm.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#f6f4f1] py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">{t('cta.title')}</h2>
            <p className="text-black/60 mt-2">{t('cta.sub')}</p>
          </div>
          <Link to="/contact">
            <Button className="bg-black hover:bg-black/85 text-white rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">{t('cta.btn')}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
